import {
  type AcceptedInvitation,
  acceptedInvitationSchema,
  type CreateInvitationRequest,
  type CreateInvitationResponse,
  createInvitationResponseSchema,
  IDENTITY_ERROR_CODES,
  type Invitation,
  type InvitationLookup,
  invitationLookupSchema,
  invitationSchema,
  type PendingInvitation,
  pendingInvitationSchema,
} from '@noconflict/contracts';
import { ApiRequestError, apiFetch } from '../../shared/api/http';
import { identityErrorMessage } from '../../shared/api/identity-error';
import type { Supabase } from '../../shared/api/supabase';
import { i18n } from '../../shared/i18n';

/**
 * Invitations of an org that are still open (pending or expired, both can be re-sent or revoked). RLS shows
 * all of them with `members.manage`, and a workspace manager the ones they sent. The view has no token hash.
 */
export async function listInvitations(
  client: Supabase,
  organizationId: string,
): Promise<Invitation[]> {
  const { data, error } = await client
    .from('invitations_view')
    .select('*')
    .eq('organization_id', organizationId)
    .in('status', ['pending', 'expired'])
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return invitationSchema.array().parse(data);
}

/** Creation goes through the worker because it sends the email; the RPC behind it decides who may invite whom. */
export function createInvitation(
  token: string,
  organizationId: string,
  input: CreateInvitationRequest,
): Promise<CreateInvitationResponse> {
  return apiFetch(`/api/orgs/${organizationId}/invitations`, createInvitationResponseSchema, {
    method: 'POST',
    token,
    body: JSON.stringify(input),
  });
}

/** A fresh link and a fresh 14 days; the old link stops working. */
export function resendInvitation(
  token: string,
  organizationId: string,
  invitationId: string,
): Promise<CreateInvitationResponse> {
  return apiFetch(
    `/api/orgs/${organizationId}/invitations/${invitationId}/resend`,
    createInvitationResponseSchema,
    { method: 'POST', token },
  );
}

export async function revokeInvitation(client: Supabase, invitationId: string): Promise<void> {
  const { error } = await client.rpc('revoke_invitation', { p_invitation_id: invitationId });
  if (error) throw new Error(error.message);
}

/** What the invite page shows whoever holds the link (works signed out). Null for an unknown token. */
export async function getInvitation(
  client: Supabase,
  token: string,
): Promise<InvitationLookup | null> {
  const { data, error } = await client.rpc('get_invitation', { p_token: token });
  if (error) throw new Error(error.message);
  const rows = invitationLookupSchema.array().parse(data ?? []);
  return rows[0] ?? null;
}

/** Live invitations addressed to the caller's confirmed email. */
export async function listMyPendingInvitations(client: Supabase): Promise<PendingInvitation[]> {
  const { data, error } = await client.rpc('my_pending_invitations');
  if (error) throw new Error(error.message);
  return pendingInvitationSchema.array().parse(data ?? []);
}

function firstAccepted(data: unknown): AcceptedInvitation {
  const rows = acceptedInvitationSchema.array().parse(data ?? []);
  const row = rows[0];
  if (!row) throw new Error('invitation_not_found');
  return row;
}

export async function acceptInvitation(
  client: Supabase,
  token: string,
): Promise<AcceptedInvitation> {
  const { data, error } = await client.rpc('accept_invitation', { p_token: token });
  if (error) throw new Error(error.message);
  return firstAccepted(data);
}

export async function acceptMyInvitation(
  client: Supabase,
  invitationId: string,
): Promise<AcceptedInvitation> {
  const { data, error } = await client.rpc('accept_my_invitation', {
    p_invitation_id: invitationId,
  });
  if (error) throw new Error(error.message);
  return firstAccepted(data);
}

const identityCodes = new Set<string>(IDENTITY_ERROR_CODES);

/**
 * Catalogue text for a failed create or resend. The worker passes identity refusals through as its error code;
 * `invitation_email_failed` means the invitation exists and the email did not go out.
 */
export function invitationErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'invitation_email_failed')
      return i18n.t('errors:api.invitation_email_failed');
    if (identityCodes.has(error.code)) return identityErrorMessage(new Error(error.code));
    return error.message;
  }
  return identityErrorMessage(error);
}
